import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { getVersion } from "@tauri-apps/api/app";
import { isTauri } from "@tauri-apps/api/core";
import { LayoutDashboard, List, Settings, UserCircle } from "lucide-react";
import { cn } from "../lib/cn";

export type NavId = "dashboard" | "subscriptions" | "settings" | "account";

type Props = {
  active: NavId;
  onNavigate: (id: NavId) => void;
  children: ReactNode;
};

export function AppShell({ active, onNavigate, children }: Props) {
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const desktop = isTauri();

  useEffect(() => {
    if (!desktop) return;
    void getVersion().then(setAppVersion);
  }, [desktop]);

  const titleWithVersion =
    desktop && appVersion != null ? `Dripwatch ${appVersion}` : "Dripwatch";

  const nav = (
    <nav className="flex flex-col gap-1" aria-label="Main">
      <NavButton
        id="dashboard"
        label="Dashboard"
        icon={<LayoutDashboard className="h-5 w-5" />}
        isActive={active === "dashboard"}
        onClick={() => onNavigate("dashboard")}
      />
      <NavButton
        id="subscriptions"
        label="Subscriptions"
        icon={<List className="h-5 w-5" />}
        isActive={active === "subscriptions"}
        onClick={() => onNavigate("subscriptions")}
      />
      <NavButton
        id="settings"
        label="Settings"
        icon={<Settings className="h-5 w-5" />}
        isActive={active === "settings"}
        onClick={() => onNavigate("settings")}
      />
      <NavButton
        id="account"
        label="Account"
        icon={<UserCircle className="h-5 w-5" />}
        isActive={active === "account"}
        onClick={() => onNavigate("account")}
      />
    </nav>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-56 shrink-0 border-r border-border bg-surface-1/50 p-4 backdrop-blur-md md:flex md:flex-col">
        <div className="mb-8 px-2">
          <p className="font-display text-lg font-bold tracking-tight text-text">
            {titleWithVersion}
          </p>
        </div>
        {nav}
        <div className="mt-auto pt-8 text-[11px] leading-relaxed text-text-muted">
          {desktop ? null : (
            <>
              Guest mode keeps your list in this browser only. When you sign in with
              cloud enabled, the same list syncs to your account.
            </>
          )}
        </div>
      </aside>

      <div className="flex min-h-screen min-h-[100dvh] flex-1 flex-col">
        <header className="sticky top-0 z-40 border-b border-border bg-surface-0/90 pt-[max(0.75rem,env(safe-area-inset-top))] backdrop-blur-md md:hidden">
          <div className="flex items-center px-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
            <p className="min-w-0 truncate font-display text-base font-semibold text-text">
              {titleWithVersion}
            </p>
          </div>
          <nav
            className="scrollbar-none overflow-x-auto overscroll-x-contain pb-2 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-2"
            aria-label="Main"
          >
            <div className="flex w-max gap-1.5">
              <MobileNavPill
                isActive={active === "dashboard"}
                onClick={() => onNavigate("dashboard")}
              >
                Home
              </MobileNavPill>
              <MobileNavPill
                isActive={active === "subscriptions"}
                onClick={() => onNavigate("subscriptions")}
              >
                Subscriptions
              </MobileNavPill>
              <MobileNavPill
                isActive={active === "settings"}
                onClick={() => onNavigate("settings")}
              >
                Settings
              </MobileNavPill>
              <MobileNavPill
                isActive={active === "account"}
                onClick={() => onNavigate("account")}
              >
                Account
              </MobileNavPill>
            </div>
          </nav>
        </header>

        <main className="flex-1 px-[max(1rem,env(safe-area-inset-left))] py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pr-[max(1rem,env(safe-area-inset-right))] md:px-8 md:py-8 md:pb-8">
          {children}
        </main>
      </div>
    </div>
  );
}

function MobileNavPill(props: {
  children: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-current={props.isActive ? "page" : undefined}
      className={cn(
        "touch-manipulation whitespace-nowrap rounded-lg px-3.5 py-2.5 text-sm font-medium outline-none focus-visible:outline-none",
        props.isActive
          ? "bg-surface-2 text-text shadow-sm ring-2 ring-accent ring-offset-2 ring-offset-surface-1"
          : cn(
              "border border-border/80 bg-surface-1 text-text-muted",
              "focus-visible:ring-2 focus-visible:ring-accent/45 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0",
            ),
      )}
    >
      {props.children}
    </button>
  );
}

function NavButton(props: {
  id: string;
  label: string;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      id={props.id}
      aria-current={props.isActive ? "page" : undefined}
      onClick={props.onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors",
        "outline-none focus-visible:outline-none",
        props.isActive
          ? "bg-surface-2 text-text shadow-sm ring-2 ring-accent ring-offset-2 ring-offset-surface-1"
          : cn(
              "text-text-muted hover:bg-surface-2/60 hover:text-text",
              "focus-visible:ring-2 focus-visible:ring-accent/45 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1",
            ),
      )}
    >
      {props.icon}
      {props.label}
    </button>
  );
}
